'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { faGripDotsVertical } from '@fortawesome/pro-regular-svg-icons/faGripDotsVertical';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { Tooltip } from '@osmosis/tailwind-ui/components/tooltip/Tooltip';
import { bg0, bgBlue } from '@osmosis/tailwind-ui/styles/background';
import { text5, textBlue } from '@osmosis/tailwind-ui/styles/text';
import { useAIPanelContext } from '../../context/AIPanelContext';
import { usePagesContext } from '@osmosis/nextjs-components/context/PagesProvider';
import { track } from '@osmosis/nextjs-components/util/tracking';
import { useColors } from '@osmosis/tailwind-ui/helpers/useColors';
import { ZIndex } from '@osmosis/tailwind-ui/helpers/zIndex';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import {
  borderColorBlue600,
  borderNeutral,
} from '@osmosis/tailwind-ui/styles/border';

const trackResizeEvent = (pathname: string | null) => {
  const panelEl = document.getElementById('ai-panel-sidebar');
  const contentEl = document.getElementById('ai-panel-content');
  const panelWidth = panelEl?.offsetWidth;
  const contentWidth = contentEl?.offsetWidth;
  const screenWidth: number = window.innerWidth;
  track(
    'osmosis-ai-embed-resized',
    { page: pathname, screenWidth, panelWidth, contentWidth },
    ['keen', 'pendo'],
  );
};

/**
 * Drag handle that sits between the page content and the panel.
 * Shows a visible resizer bar and a collapse button on hover.
 */
const PanelResizer = () => {
  const { formatMessage } = useIntl();
  const colors = useColors();
  const { panelWidth, setPanelWidth, setIsDragging } = useAIPanelContext();
  const isDragging = useRef(false);
  const [isActive, setIsActive] = useState(false);
  const activeMoveRef = useRef<((e: PointerEvent) => void) | null>(null);
  const activeUpRef = useRef<((e: PointerEvent) => void) | null>(null);
  const { pathname } = usePagesContext();

  const resizeHandleLabel = formatMessage({
    id: 'panel.resize-handle',
    defaultMessage: 'Drag to resize',
  });

  // Safety-net: if the component unmounts mid-drag, remove listeners and restore cursor.
  useEffect(() => {
    return () => {
      document.body.style.cursor = '';
      setIsDragging(false);
      if (activeMoveRef.current)
        window.removeEventListener('pointermove', activeMoveRef.current);
      if (activeUpRef.current)
        window.removeEventListener('pointerup', activeUpRef.current);
    };
  }, [setIsDragging]);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      isDragging.current = true;
      setIsActive(true);
      setIsDragging(true);

      const startX = e.clientX;
      const startWidth = panelWidth;

      const onPointerMove = (moveEvent: PointerEvent) => {
        if (!isDragging.current) return;
        const delta = startX - moveEvent.clientX;
        setPanelWidth(startWidth + delta);
      };

      const onPointerUp = () => {
        isDragging.current = false;
        setIsActive(false);
        setIsDragging(false);
        document.body.style.cursor = '';
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
        activeMoveRef.current = null;
        activeUpRef.current = null;
        trackResizeEvent(pathname);
      };

      activeMoveRef.current = onPointerMove;
      activeUpRef.current = onPointerUp;
      document.body.style.cursor = 'ew-resize';
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
    },
    [panelWidth, pathname, setPanelWidth, setIsDragging],
  );

  return (
    <Flex
      className="group relative hidden w-6 flex-shrink-0 cursor-ew-resize touch-none select-none items-start justify-center self-stretch md:flex"
      onMouseEnter={() => setIsActive(true)}
      onMouseLeave={() => {
        if (!isDragging.current) setIsActive(false);
      }}
      onPointerDown={handlePointerDown}
      data-testid="panel-resizer"
      aria-hidden="true"
    >
      {/* Visible vertical bar — spans the full height behind the grip pill. */}
      <Box
        data-testid="panel-resizer-line"
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute -top-4 bottom-0 left-1/2 w-0.5 -translate-x-1/2 transition-colors duration-150',
          isActive ? bgBlue : 'bg-neutral-400',
        )}
      />
      <Box
        data-testid="panel-resizer-bar"
        data-tooltip-id="panel-resize-tooltip"
        className={cn(
          'sticky top-[33vh] flex h-9 w-4 items-center justify-center rounded-md border transition-colors duration-150',
          bg0,
          isActive ? borderColorBlue600 : borderNeutral,
        )}
      >
        <Icon
          icon={faGripDotsVertical}
          size={16}
          className={isActive ? textBlue : text5}
        />
      </Box>
      <Tooltip
        id="panel-resize-tooltip"
        style={{
          backgroundColor: colors.text,
          color: colors.textInvert,
          zIndex: ZIndex.MODAL,
        }}
      >
        <P color="inherit" className="text-[13px]">
          {resizeHandleLabel}
        </P>
      </Tooltip>
    </Flex>
  );
};

export default PanelResizer;
